import { StackContextManager, WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import {
  diag,
  DiagConsoleLogger,
  DiagLogLevel,
  ROOT_CONTEXT,
  trace,
  type Attributes,
  type Context,
  type Span,
} from '@opentelemetry/api';
import { W3CTraceContextPropagator } from '@opentelemetry/core';
import { resourceFromAttributes } from '@opentelemetry/resources';
import {
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_NAMESPACE,
  ATTR_SERVICE_VERSION,
  ATTR_URL_FULL,
} from '@opentelemetry/semantic-conventions';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { NavigationSpanProcessor, type NavigationSpanProcessorCallbacks } from './navigation-span-processor';

// These attributes live in the @opentelemetry/semantic-conventions/incubating subpath export which requires
// moduleResolution "bundler" or "node16+".  The monorepo uses "node" so we
// inline the semconv constants to avoid a TS2307 resolution failure.
const ATTR_DEPLOYMENT_ENVIRONMENT = 'deployment.environment' as const;
const ATTR_BROWSER_PLATFORM = 'browser.platform' as const;
const ATTR_BROWSER_LANGUAGE = 'browser.language' as const;
const ATTR_HTTP_URL = 'http.url' as const;

const SERVICE_NAME = 'Nestech.Cassiopeia.WebClient';
const TRACE_EXPORT_PATH = '/otlp/v1/traces';

/** The only requests this app traces — the single source for the instrumentations and the span processor. */
const TRACKED_PATH_PREFIXES = ['/api/', '/proxy/'];

const ID_PLACEHOLDER = '{id}';
const GUID_SEGMENT_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_SEGMENT_PATTERN = /^\d+$/;

/** `http.url` is the old semantic convention, `url.full` the current one; a span can carry either. */
const URL_ATTRIBUTE_KEYS = [ATTR_HTTP_URL, ATTR_URL_FULL];

// Navigation context injected into active() so FetchInstrumentation
// parents HTTP spans under the navigation span across async boundaries.
let navigationCtx: Context | null = null;

class NavContextManager extends StackContextManager {
  // Substituting the navigation context whenever the delegate has none is what parents fetch/XHR
  // spans under the navigation; it also re-parents a span the caller detached on purpose with
  // context.with(ROOT_CONTEXT, …).
  override active(): Context {
    const ctx = super.active();
    return ctx !== ROOT_CONTEXT ? ctx : (navigationCtx ?? ROOT_CONTEXT);
  }
}

let navigationSpanProcessor: NavigationSpanProcessor;
let telemetryInitialized = false;

export interface OtelSystemInfo {
  isEnabled: boolean;
}

export interface TelemetryInitConfig {
  otel?: OtelSystemInfo;
  deploymentEnvironment?: string;
  /** Distinguishes the portals that share `service.name`; `Environment.companyName` supplies it. */
  companyName?: string;
  appVersion?: string;
}

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Matches every URL that must not be traced. The instrumentations take only strings and regexes, so
 * the same-origin half of the rule `isAllowedTracingUrl` states has to be baked into the pattern:
 * fetch is handed an absolute URL and XHR the raw, root-relative one, so both forms have to pass.
 */
const untrackedUrlPattern = (): RegExp => {
  const origin = escapeRegExp(window.location.origin);
  const prefixes = TRACKED_PATH_PREFIXES.map(escapeRegExp).join('|');
  return new RegExp(`^(?!(?:${origin})?(?:${prefixes}))`);
};

/**
 * The URL as it may be recorded on a span: query string dropped and every id-shaped path segment
 * replaced, so the endpoint stays identifiable while the member does not. The query goes wholesale
 * rather than per parameter because the sensitive values here are business identifiers under
 * arbitrary names (`umr`, `draftGroupId`, `hash`, `cardno`), not a known credential list.
 */
export const sanitizeSpanUrl = (url: string): string =>
  url
    .split(/[?#]/)[0]
    .split('/')
    .map(segment =>
      GUID_SEGMENT_PATTERN.test(segment) || NUMERIC_SEGMENT_PATTERN.test(segment) ? ID_PLACEHOLDER : segment,
    )
    .join('/');

const sanitizeRecordedUrls = (span: Span): void => {
  const attributes = (span as Span & { attributes?: Attributes }).attributes;
  for (const key of URL_ATTRIBUTE_KEYS) {
    const recorded = attributes?.[key];
    if (typeof recorded === 'string') span.setAttribute(key, sanitizeSpanUrl(recorded));
  }
};

export const createHttpInstrumentationConfig = () => ({
  // Cross-origin requests only — same-origin ones always carry `traceparent`. Nothing off-origin may
  // get it: it leaks the trace id and forces a CORS preflight the third party need not allow, which
  // fails the request outright. That is the rule `isAllowedTracingUrl` states, as a list.
  propagateTraceHeaderCorsUrls: [] as RegExp[],
  ignoreUrls: [untrackedUrlPattern()],
  applyCustomAttributesOnSpan: sanitizeRecordedUrls,
});

/**
 * BatchSpanProcessor ships ended spans only, on a 5s schedule, so without this the last batch of
 * every session is discarded and the navigation the member abandoned is never exported at all. The
 * exporter posts with `fetch(keepalive)`, which survives unload.
 */
const flushOnPageHide = (tracerProvider: WebTracerProvider): void => {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void tracerProvider.forceFlush();
  });
  window.addEventListener('pagehide', () => {
    const navigationSpan = navigationCtx ? trace.getSpan(navigationCtx) : undefined;
    navigationSpan?.addEvent('page-hide');
    navigationSpan?.end();
    void tracerProvider.forceFlush();
  });
};

function startTracing(config: TelemetryInitConfig) {
  // Without a logger the exporter's own failure reports go nowhere, and a misrouted /otlp/ is
  // indistinguishable from a working one.
  diag.setLogger(new DiagConsoleLogger(), DiagLogLevel.ERROR);

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: SERVICE_NAME,
    ...(config.companyName && { [ATTR_SERVICE_NAMESPACE]: config.companyName }),
    ...(config.appVersion && { [ATTR_SERVICE_VERSION]: config.appVersion }),
    ...(config.deploymentEnvironment && { [ATTR_DEPLOYMENT_ENVIRONMENT]: config.deploymentEnvironment }),
    [ATTR_BROWSER_PLATFORM]:
      (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ??
      navigator.platform,
    [ATTR_BROWSER_LANGUAGE]: navigator.language,
    'browser.user_agent': navigator.userAgent,
  });

  const traceExporter = new OTLPTraceExporter({ url: TRACE_EXPORT_PATH });

  navigationSpanProcessor = new NavigationSpanProcessor(null, TRACKED_PATH_PREFIXES);

  const tracerProvider = new WebTracerProvider({
    resource,
    spanProcessors: [
      navigationSpanProcessor,
      new BatchSpanProcessor(traceExporter, {
        scheduledDelayMillis: 5000,
        exportTimeoutMillis: 30000,
        maxExportBatchSize: 30,
        maxQueueSize: 2048,
      }),
    ],
  });

  tracerProvider.register({ contextManager: new NavContextManager(), propagator: new W3CTraceContextPropagator() });

  const instrumentationConfig = createHttpInstrumentationConfig();

  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation(instrumentationConfig),
      new XMLHttpRequestInstrumentation(instrumentationConfig),
    ],
  });

  flushOnPageHide(tracerProvider);
}

export function initializeTelemetry(config?: TelemetryInitConfig) {
  if (telemetryInitialized) return;
  if (!config?.otel?.isEnabled) return;

  // A backend flag turns this on and the caller is awaited before bootstrapApplication, so anything
  // thrown here would leave every member of every portal with a blank page.
  try {
    startTracing(config);
    telemetryInitialized = true;
    console.debug('OpenTelemetry initialized (traces)');
  } catch (error) {
    console.error('OpenTelemetry initialization failed, continuing without telemetry', error);
  }
}

export function isTelemetryInitialized(): boolean {
  return telemetryInitialized;
}

export function setNavigationContext(ctx: Context | null): void {
  navigationCtx = ctx;
}

export function setNavigationSpanProcessorCallbacks(callbacks: NavigationSpanProcessorCallbacks): void {
  navigationSpanProcessor?.setCallbacks(callbacks);
}
